import {
    ComponentObject,
    ComponentSelectors,
    ComponentElements,
    ComponentInjector,
    UIComponent,
    ComponentService,
} from "@atypon/ui-utils";

class Texts extends ComponentObject {
    doi = "";
    apiUrl = "";
    doiUpdatesUrl = "/action/fetchDoiUpdates";
    isCrossrefEnabled = "";
    isRetractionsEnabled = "";
    getFTRContent = "";
    getFTRButton = "";
    getFTRLink = "";
    prevItemDoi = "";
    requestSource = "";
    location = '';
    citedBy = 'CitedBy';
    reference = 'Reference';
}

class ClassList extends ComponentObject {
    getFTRPlaceholder = "getFTR__placeholder";
}

class Selectors extends ComponentSelectors {
    article = "article";
    articleReferences = "article .article__references";
    articleCitedBy = "article .citedBySection";
    tabContent = ".tab--slide .tab__content";
    tabReferences = "#pane-pcw-references";
    tabRelated = "#pane-pcw-related";
    getFTRPlaceholder = ".getFTR__placeholder";
    getFTRContent = ".getFTR__content";
    wrapperItem = ""; // Reference or citedBy item
}

class Elements extends ComponentElements<Selectors> {
    article: HTMLElement;
    articleReferences: HTMLElement;
    articleCitedBy: HTMLElement;
    tabContent: HTMLElement;
    tabReferences: HTMLElement;
    tabRelated: HTMLElement;
    getFTRWrapper: HTMLElement;
    getFTRPlaceholder: HTMLElement;
    source: HTMLElement; // References tab, related tab, article references or article citedBy
}

interface GetFTR
    extends ComponentService<Selectors, Elements, ClassList, Texts> {
}

@ComponentInjector(Selectors, Elements, ClassList, Texts)
class GetFTR implements UIComponent {
    isButtonVisible: boolean = false;
    linksCount: number;
    numberOfPlaceholders: number;
    isSafeToFetch: boolean = true;

    initialize(): void {
        this.setElements();
        this.addEventListeners();
        this.getGetFTRConfig();
    }

    private setElements(): void {
        this.elements.articleReferences = this.domUtils.getElement<HTMLElement>(
            this.selectors.articleReferences,
        );
        this.elements.articleCitedBy = this.domUtils.getElement<HTMLElement>(
            this.selectors.articleCitedBy,
        );
        this.elements.tabContent = this.domUtils.getElement<HTMLElement>(
            this.selectors.tabContent,
        );
    }

    getGetFTRConfig(): void {
        this.elements.article = this.domUtils.getElement<HTMLElement>(
            this.selectors.article,
        );
        this.texts.isRetractionsEnabled = this.elements.article.getAttribute(
            "data-getFTR-retractions-enabled",
        );
        this.texts.isCrossrefEnabled = this.elements.article.getAttribute(
            "data-crossref-enabled",
        );
        this.texts.requestSource = this.elements.article.getAttribute(
            "data-getFTR-request-source",
        );
        this.texts.doi = this.elements.article.getAttribute(
            "data-getFTR-doi",
        );
        this.linksCount = parseInt(
            this.elements.article.getAttribute("data-getFTR-links-count"),
        );
    }

    addEventListeners(): void {
        window.addEventListener("scroll", this.handleWindowScroll);
    }

    private getGetFTRElements(): void {
        const placeholderElements = this.domUtils
            .getElements(this.selectors.getFTRPlaceholder, this.elements.source)
            .slice(0, this.linksCount);
        const firstPlaceholderElement =
            placeholderElements && placeholderElements[0];
        const firstItemDoi = firstPlaceholderElement
            ?.closest("[data-doi]")
            ?.getAttribute("data-doi");
        const elementsData = placeholderElements
            ?.map(
                (element) =>
                    element.closest(this.selectors.wrapperItem)?.getAttribute("data-doi"),
            )
            .filter(Boolean); // to remove any null or undefined values from the array

        if (
            elementsData?.length > 0 &&
            this.numberOfPlaceholders &&
            firstItemDoi !== this.texts.prevItemDoi
        ) {
            this.texts.prevItemDoi = firstItemDoi;
            this.fetchData(elementsData);
            this.texts.isRetractionsEnabled && this.fetchDoiUpdates(elementsData, this.texts.location)
            this.elements.tabContent?.addEventListener("scroll", () => {
                this.handleTabScroll(this.elements.source);
            });
        }
    }

    private setGetFTRSource(
        sourceElement: HTMLElement,
        wrapperItemSelector: string,
        apiUrl: string,
    ): void {
        this.initialize();
        this.elements.source = sourceElement;
        this.selectors.wrapperItem = wrapperItemSelector;
        this.numberOfPlaceholders =
            this.elements.source?.querySelectorAll<HTMLElement>(
                this.selectors.getFTRPlaceholder,
            ).length;
        this.texts.apiUrl = this.elements.article.getAttribute(apiUrl);
        this.getGetFTRElements();
    }

    referencesTab(): void {
        this.elements.tabReferences = this.domUtils.getElement<HTMLElement>(
            this.selectors.tabReferences,
        );
        this.texts.location = this.texts.reference;
        this.setGetFTRSource(
            this.elements.tabReferences,
            ".references__item",
            "data-getFTR-reference-url",
        );
        this.handleTooltipPosition();
    }

    relatedTab(): void {
        this.elements.tabRelated = this.domUtils.getElement<HTMLElement>(
            this.selectors.tabRelated,
        );
        this.texts.location = this.texts.citedBy;
        this.setGetFTRSource(
            this.elements.tabRelated,
            ".citedByEntry",
            "data-getFTR-citedby-url",
        );
    }

    articleReferences(): void {
        this.texts.location = this.texts.reference;
        this.setGetFTRSource(
            this.elements.articleReferences,
            ".references__item",
            "data-getFTR-reference-url",
        );
    }

    articleCitedBy(): void {
        this.texts.location = this.texts.citedBy;
        this.setGetFTRSource(
            this.elements.articleCitedBy,
            ".citedByEntry",
            "data-getFTR-citedBy-url",
        );
    }

    private handleTabScroll(source: HTMLElement): void {
        const firstPlaceholder = source?.querySelector<HTMLElement>(
            this.selectors.getFTRPlaceholder,
        );
        const tabContentScrollY = this.elements.tabContent?.scrollTop;
        const firstPlaceholderOffsetTop = firstPlaceholder?.getBoundingClientRect().top - this.elements.tabContent?.getBoundingClientRect().top + tabContentScrollY;

        if (
            firstPlaceholder &&
            firstPlaceholderOffsetTop - 500 < tabContentScrollY &&
            this.isSafeToFetch
        ) {
            this.getGetFTRElements();
        }
    }

    private handleWindowScroll = (): void => {
        if (!this.elements.articleReferences && !this.elements.articleCitedBy) {
            return;
        }

        const scrollPosition = window.scrollY;
        const articleCitedByOffsetTop = this.elements.articleCitedBy?.offsetTop;
        const targetElement =
            scrollPosition >= articleCitedByOffsetTop
                ? this.elements.articleCitedBy
                : this.elements.articleReferences;

        const firstPlaceholder = targetElement?.querySelector<HTMLElement>(
            this.selectors.getFTRPlaceholder,
        );
        const rect = firstPlaceholder?.getBoundingClientRect();
        const firstPlaceholderOffsetTop = rect?.top + window.scrollY;

        if (
            firstPlaceholder &&
            this.isSafeToFetch &&
            firstPlaceholderOffsetTop - 500 < scrollPosition
        ) {
            if (targetElement === this.elements.articleReferences) {
                this.articleReferences();
            } else {
                this.articleCitedBy();
            }
        }
    };

    private fetchData(elementsData: string[]): void {
        const requestBody = {
            pubDoi: this.texts.doi,
            associatedCitations: elementsData,
            requestSource: this.texts.requestSource,
        };

        this.isSafeToFetch = false;

        fetch(this.texts.apiUrl, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
        })
            .then((response) => response.json())
            .then((results) => {
                results.forEach((item: object) => {
                    this.getMatchingElement(item)
                    this.isEntitlement(item);
                    this.getFTRContentEmpty();
                    this.removePlaceholder();
                });
            })
            .catch((error) => {
                console.error("Error:", error);
            })
            .finally(() => {
                this.isSafeToFetch = true;
            });
    }

    private async fetchDoiUpdates(dois: string[], location: string): Promise<void> {
        const requestBody = {
            pubDoi: this.texts.doi,
            associatedCitations: dois,
            requestSource: this.texts.requestSource,
            location: location
        };

        try {
            const response = await fetch(this.texts.doiUpdatesUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(requestBody),
            });

            const results = await response.json();
            for (const item of results) {
                this.getMatchingElement(item)
                this.getFTRDoiUpdates(item);
            }
        } catch (error) {
            console.error("Error:", error);
        }
    }

    private getMatchingElement = (item: object) => {
        this.elements.getFTRWrapper = this.elements.source?.querySelector(`[data-doi="${item["doi"]}"]`); // Reference or cited by element
        this.elements.getFTRPlaceholder = this.elements.getFTRWrapper?.querySelector(this.selectors.getFTRContent);
    }

    private isEntitlement = (item: object): void => {
        if (item["entitlement"]) {
            this.isButtonVisible = true;
            this.getFTRButton(item["entitlement"]);
        } else if (
            !this.texts.isCrossrefEnabled &&
            item["doi"] &&
            item["fallback"]
        ) {
            this.isButtonVisible = true;
            this.getFTRButton(item["fallback"]["url"]);
        } else if (this.texts.isCrossrefEnabled && item["fallback"]) {
            this.getFTRLink(item["fallback"]);
        }
    };

    private getFTRButton = (data: object | string): void => {
        let isIconVisible = false;
        let iconName = "";
        let iconEl = "";
        const isEntitlement = typeof data === "object";
        const btnText = isEntitlement ? data["label"] : "View";
        const btnLink = isEntitlement ? data["url"] : data;
        const tooltip = isEntitlement && data["tooltip"] ? data["tooltip"] : false;

        if (
            isEntitlement &&
            (data["entitled"] === "yes" ||
                (data["entitled"] === "maybe" && data["type"] === "vor"))
        ) {
            isIconVisible = true;
            iconName = "icon-getFTR-na";
        } else if (
            isEntitlement &&
            data["entitled"] === "no" &&
            data["type"] === "av"
        ) {
            isIconVisible = true;
            iconName = "icon-getFTR-av";
        }

        iconEl = isIconVisible
            ? `<i aria-hidden="true" class="${iconName}"></i>`
            : "";
        this.texts.getFTRButton = `<a href="${btnLink}"  aria-label="${tooltip ? tooltip : btnText
        }" target="_blank" data-tooltip="${tooltip}" class="getFTR__btn ${tooltip ? "getFTR__tooltip" : ""
        }"  tabindex="0">
               ${iconEl}
               ${btnText}
            </a>`;

        this.elements.getFTRPlaceholder?.insertAdjacentHTML(
            "afterbegin",
            this.texts.getFTRButton,
        );
    };

    private getFTRLink = (data: object): void => {
        this.texts.getFTRLink = `<a href="${data["url"]}"  target="_blank" class="getFTR__link">
               ${data["label"]}
            </a>`;

        this.elements.getFTRPlaceholder?.insertAdjacentHTML(
            "afterbegin",
            this.texts.getFTRLink,
        );
    };

    private getFTRDoiUpdates = (data: object) => {
        const type = data['updateType'] === 'retraction' ? 'retraction' : 'updates';
        const url = data['url'];
        const tooltipTitle = data['updateType'] === 'retraction' ? `This document was retracted at ${data['updateDate']}` : `This document has been updated`;
        const tooltipList = data['reasons'].length ? `<b>Reasons:</b><ul class="list">${data['reasons'].map(reason => `<li>${reason}</li>`).join('')}</ul>` : '';
        const tooltipHint = "Click for further information.";
        const getFTRDoiUpdatesTooltip = this.elements.getFTRPlaceholder?.querySelector('.getFTR__doiUpdates-tooltip');

        const htmlContent = `
        <a href="${url}" class="getFTR__btn getFTR__doiUpdates-btn ${type}" target="_blank" tabindex="0">
          <i aria-hidden="true" class="icon icon-warning-triangle ${type}"></i> ${type}
        </a>
        <div class="getFTR__doiUpdates-tooltip">
          <i aria-hidden="true" class="icon icon-warning-triangle ${type}"></i>
          <div>
            <p class="title">${tooltipTitle}</p>
            ${tooltipList}
            <p class="hint">${tooltipHint}</p>
          </div>
        </div>
      `;

        if (this.elements.getFTRPlaceholder && !getFTRDoiUpdatesTooltip) {
            this.elements.getFTRPlaceholder.insertAdjacentHTML(
                "afterend",
                htmlContent,
            );
        }
    }

    private handleTooltipPosition = () => {
        const doiUpdatesButtons = this.domUtils.getElements<HTMLElement>('.getFTR__doiUpdates-btn');
        if (!doiUpdatesButtons.length) return;

        doiUpdatesButtons.forEach((button) => {
            if (button.getAttribute('data-button-entered')) return;

            const handlePosition = () => {
                const tabReferences = this.domUtils.getElement<HTMLElement>(this.selectors.tabReferences);
                const tooltip = button.nextElementSibling as HTMLElement;

                if (!tabReferences || !tooltip) return;

                const tabReferencesTop = tabReferences.getBoundingClientRect().top;
                const tooltipTop = tooltip.getBoundingClientRect().top;

                if (tabReferencesTop > tooltipTop) {
                    tooltip.classList.add('bottom');
                }
            };

            button.addEventListener('mouseenter', handlePosition, {once: true});
            button.addEventListener('touchstart', handlePosition, {passive: true, once: true});
            button.setAttribute('data-button-entered', 'true');
        });
    };

    private getFTRContentEmpty(): void {
        this.texts.getFTRButton = "";
        this.texts.getFTRLink = "";
        this.isButtonVisible = false;
    }

    private removePlaceholder(): void {
        this.elements.getFTRPlaceholder?.classList.remove("getFTR__placeholder");
    }
}

export default GetFTR;

export {
    Elements as GetFTRElements,
    Selectors as GetFTRSelectors,
    ClassList as GetFTRClassList,
    Texts as GetFTRTexts,
};
